import {
  useRichTextEditor,
  type RteContentProps as ContentProps,
} from '@shared/rich-text-editor-core';
import { EditorContent } from '@tiptap/react';
import React, { forwardRef, useEffect } from 'react';
import './rte.css';

export function TailwindRoot({ error, children }: { error?: boolean; children?: React.ReactNode }) {
  return (
    <div
      className={[
        'rte-root relative flex flex-col rounded-lg',
        error
          ? 'border-red-400 ring-1 ring-red-300'
          : 'focus-within:ring-1 focus-within:ring-neutral-300',
        'bg-white transition-shadow',
      ].join(' ')}
    >
      {children}
    </div>
  );
}

export const TailwindContent = forwardRef<HTMLDivElement, ContentProps>(
  (props: ContentProps, ref) => {
    const { rows = 1, onBlur } = props;
    const { editor } = useRichTextEditor();

    // Basic min-height heuristic; tailwind default text-base ~16px * 1.5 line-height + padding
    const minHeight = Math.max(rows, 1) * 16 * 1.5 + 16 * 2;

    useEffect(() => {
      if (!editor || !onBlur) return;
      const listener = () => {
        onBlur();
      };
      editor.on('blur', listener);
      return () => {
        editor.off('blur', listener);
      };
    }, [editor, onBlur]);

    if (!editor) return null;

    return (
      <div className='rte-content relative'>
        <EditorContent
          ref={ref}
          editor={editor}
          className='max-w-none rounded-b bg-white p-4 text-neutral-800 focus:outline-none'
          style={{ minHeight }}
        />
      </div>
    );
  },
);

TailwindContent.displayName = 'TailwindContent';

export function TailwindLoadingOverlay() {
  return (
    <div className='pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/60'>
      <div className='border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent' />
      <span className='sr-only'>Loading…</span>
    </div>
  );
}

export default {
  TailwindRoot,
  TailwindContent,
  TailwindLoadingOverlay,
};
